import { useEffect, useState } from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { StandardIngredient } from '../../types/ingredients';
import {
  getIngredientUsageApi,
  rejectIngredientApi,
  IngredientUsageRecipe,
} from '../../utils/ingredientsModerationApi';

interface IngredientRejectModalProps {
  ingredient: StandardIngredient;
  onClose: () => void;
  onSuccess: (info: { recipesUpdated: number }) => void;
}

export function IngredientRejectModal({
  ingredient,
  onClose,
  onSuccess,
}: IngredientRejectModalProps) {
  const [usage, setUsage] = useState<IngredientUsageRecipe[] | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingUsage(true);
    getIngredientUsageApi(ingredient.id)
      .then((res) => {
        if (!cancelled) setUsage(res.recipes);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to fetch usage:', err);
          setError(err?.message || "Impossible de calculer l'impact.");
          setUsage([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingUsage(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ingredient.id]);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await rejectIngredientApi(ingredient.id);
      onSuccess({ recipesUpdated: result.recipesUpdated });
    } catch (err: any) {
      console.error('Reject failed:', err);
      setError(err?.message || 'Le rejet a échoué.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <h3 className="text-lg font-semibold text-menioo-navy">Rejeter l'ingrédient</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-900">
            <p>
              «&nbsp;<span className="font-medium">{ingredient.name}</span>&nbsp;» sera supprimé
              définitivement et retiré de toutes les recettes qui le référencent. Cette action est
              irréversible.
            </p>
          </div>

          <div>
            <h4 className="text-sm font-medium text-menioo-navy mb-2">Impact sur les recettes</h4>
            {loadingUsage ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-4 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" />
                Calcul de l'impact en cours…
              </div>
            ) : usage && usage.length === 0 ? (
              <p className="text-sm text-gray-500 italic">Aucune recette ne référence cet ingrédient.</p>
            ) : (
              <div className="border border-gray-200 rounded-md max-h-64 overflow-y-auto divide-y divide-gray-100">
                {usage?.map((r) => (
                  <div key={r.id} className="px-3 py-2 text-sm flex items-center justify-between">
                    <span className="truncate">{r.name}</span>
                    <span className="text-xs text-gray-400 ml-2 shrink-0">
                      {r.isPublic ? 'publique' : 'privée'}
                    </span>
                  </div>
                ))}
                <p className="px-3 py-2 text-xs text-gray-500 bg-gray-50">
                  {usage?.length} recette{(usage?.length ?? 0) > 1 ? 's' : ''} concernée
                  {(usage?.length ?? 0) > 1 ? 's' : ''}.
                </p>
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || loadingUsage}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? 'Suppression…' : 'Rejeter définitivement'}
          </button>
        </div>
      </div>
    </div>
  );
}
